import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LoaderCircle, Plus, Save, Trash2 } from "lucide-react";
import { useState } from "react";
import { type UseFormSetError, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import type { Equipo } from "@/lib/equipos";

// Espejo de la validación de servidor (factory/procedimiento_dto.go): equipo obligatorio,
// código ≤60, versión ≤30, al menos un paso ≤500 y verificación ≤2000. id_equipo se
// coacciona a número porque el <select> nativo entrega string.
export const procedimientoSchema = z.object({
  id_equipo: z.coerce.number().int().positive("Selecciona un equipo"),
  codigo_procedimiento: z
    .string()
    .trim()
    .min(1, "El código es obligatorio")
    .max(60, "Máximo 60 caracteres"),
  version_procedimiento: z
    .string()
    .trim()
    .min(1, "La versión es obligatoria")
    .max(30, "Máximo 30 caracteres"),
  pasos: z
    .array(z.object({ text: z.string().trim().min(1, "El paso no puede estar vacío").max(500) }))
    .min(1, "Añade al menos un paso"),
  verificacion_energia_cero: z.string().trim().max(2000, "Máximo 2000 caracteres"),
  activo: z.boolean(),
});

// El esquema coacciona id_equipo (z.coerce.number): el tipo de entrada difiere del de
// salida ya validado. Con @hookform/resolvers v5 hay que tipar useForm con ambos.
type ProcedimientoFormInput = z.input<typeof procedimientoSchema>;
export type ProcedimientoFormValues = z.output<typeof procedimientoSchema>;

// CAMPOS_SERVIDOR son los campos del formulario que el backend puede rechazar (VALIDACION).
const CAMPOS_SERVIDOR = [
  "id_equipo",
  "codigo_procedimiento",
  "version_procedimiento",
  "pasos",
  "verificacion_energia_cero",
] as const;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<ProcedimientoFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.code === "VALIDACION" && err.fields) {
      let mapped = false;
      for (const campo of CAMPOS_SERVIDOR) {
        const msg = err.fields[campo];
        if (msg) {
          setError(campo, { message: msg });
          mapped = true;
        }
      }
      if (mapped) {
        return;
      }
    }
    // PROCEDIMIENTO_DUPLICADO y demás errores del API muestran el mensaje del backend.
    setFormError(err.message || "No se pudo guardar el procedimiento.");
    return;
  }
  setFormError("No se pudo guardar el procedimiento. Comprueba tu conexión.");
}

/**
 * ProcedimientoForm es el formulario reutilizable de alta/edición de procedimientos LOTO
 * (react-hook-form + zod). Incluye un editor de PASOS como lista ordenada (añadir /
 * eliminar / reordenar por posición). Sigue el patrón de EquipoForm (#28) y mapea los
 * errores por campo del backend (VALIDACION).
 */
export function ProcedimientoForm({
  defaultValues,
  equipos,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  defaultValues: ProcedimientoFormValues;
  equipos: Equipo[];
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: ProcedimientoFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ProcedimientoFormInput, unknown, ProcedimientoFormValues>({
    resolver: zodResolver(procedimientoSchema),
    defaultValues,
  });
  const { fields, append, remove } = useFieldArray({ control, name: "pasos" });

  const submit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      aplicarError(err, setError, setFormError);
    }
  });

  const pasosError = errors.pasos?.root?.message ?? errors.pasos?.message;

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="id_equipo">Equipo</Label>
        <Select
          id="id_equipo"
          aria-invalid={errors.id_equipo ? true : undefined}
          {...register("id_equipo")}
        >
          <option value="0" disabled>
            Selecciona un equipo
          </option>
          {equipos.map((equipo) => (
            <option key={equipo.id} value={equipo.id}>
              {equipo.codigo_equipo} · {equipo.nombre_equipo}
            </option>
          ))}
        </Select>
        <FieldError>{errors.id_equipo?.message}</FieldError>
      </div>

      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="codigo_procedimiento">Código</Label>
          <Input
            id="codigo_procedimiento"
            autoComplete="off"
            aria-invalid={errors.codigo_procedimiento ? true : undefined}
            {...register("codigo_procedimiento")}
          />
          <FieldError>{errors.codigo_procedimiento?.message}</FieldError>
        </div>
        <div className="flex w-32 flex-col gap-1.5">
          <Label htmlFor="version_procedimiento">Versión</Label>
          <Input
            id="version_procedimiento"
            autoComplete="off"
            aria-invalid={errors.version_procedimiento ? true : undefined}
            {...register("version_procedimiento")}
          />
          <FieldError>{errors.version_procedimiento?.message}</FieldError>
        </div>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 font-medium text-slate-800 text-sm">Pasos</legend>
        {fields.map((field, index) => (
          <div key={field.id} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="w-6 shrink-0 text-right text-slate-500 text-sm tabular-nums">
                {index + 1}.
              </span>
              <Input
                aria-label={`Paso ${index + 1}`}
                autoComplete="off"
                aria-invalid={errors.pasos?.[index]?.text ? true : undefined}
                {...register(`pasos.${index}.text` as const)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Eliminar paso ${index + 1}`}
                disabled={fields.length === 1}
                onClick={() => remove(index)}
              >
                <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
              </Button>
            </div>
            <FieldError className="pl-8">{errors.pasos?.[index]?.text?.message}</FieldError>
          </div>
        ))}
        <FieldError>{pasosError}</FieldError>
        <Button
          type="button"
          variant="outline"
          className="self-start"
          onClick={() => append({ text: "" })}
        >
          <Plus className="h-5 w-5" aria-hidden="true" />
          Añadir paso
        </Button>
      </fieldset>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="verificacion_energia_cero">Verificación de energía cero (opcional)</Label>
        <Textarea
          id="verificacion_energia_cero"
          aria-invalid={errors.verificacion_energia_cero ? true : undefined}
          {...register("verificacion_energia_cero")}
        />
        <FieldError>{errors.verificacion_energia_cero?.message}</FieldError>
      </div>

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
          {...register("activo")}
        />
        <span className="text-base text-slate-800">Activo</span>
      </label>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-5 w-5" aria-hidden="true" />
          )}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
